import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { loginUser } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Lock, Mail, AlertCircle, Loader2 } from 'lucide-react';

export const Login = () => {
  const { setCurrentRole, setCurrentUserId, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await loginUser(email, password);
      if (result && result.user) {
        const apiUser = result.user;
        setCurrentRole(apiUser.role);
        setCurrentUserId(apiUser.id);
        localStorage.setItem('crm_user', JSON.stringify(apiUser));
        localStorage.setItem('crm_user_id', apiUser.id);
        localStorage.setItem('crm_role', apiUser.role);
        login();
        const target = sessionStorage.getItem('redirect_after_login') || '/';
        sessionStorage.removeItem('redirect_after_login');
        navigate(target);
      } else {
        setError(result?.message || 'Invalid email or password');
      }
    } catch (apiErr) {
      console.error('Login failed:', apiErr);
      const msg = apiErr.response?.data?.message || 'Invalid email or password';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-slate-200 p-8 animate-fade-in">
        <div className="text-center mb-8">
          <div className="font-bold text-black text-3xl tracking-tight mb-2">AD Ventures</div>
          <p className="text-slate-500 text-sm">Welcome back! Please enter your details.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="email"
                required
                placeholder="Enter your email"
                className="pl-10 bg-white border-slate-200 focus:border-blue-500/50 text-slate-900 w-full placeholder:text-slate-400"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Password</label>
              <a href="#" className="text-xs text-blue-600 hover:text-blue-700 font-medium">Forgot password?</a>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="password"
                required
                placeholder="••••••••"
                className="pl-10 bg-white border-slate-200 focus:border-blue-500/50 text-slate-900 w-full placeholder:text-slate-400"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm h-11">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Sign in
          </Button>
        </form>

        <div className="mt-8 text-center text-sm text-slate-500">
          Don't have an account? <a href="#" className="text-blue-600 hover:text-blue-700 font-semibold">Contact Administrator</a>
        </div>
      </div>
    </div>
  );
};

export default Login;
